import { UIIcon } from '../../config/icons';
import { yResolve } from '../y/util';
import { UIDropdown, UIDropdownItem } from './component/dropdown';
import { $el, UILink } from './elements';
import { uiShowPopup } from './popup';
import { UIRelPos, UIValue } from './types';
import { UIViewAddSpec, UIViewSpec } from './view';

export interface UIMenuItemBase {
    key?: string;
    when?: (() => boolean) | boolean;
    data?: any;
}

export interface UISeparatorMenuItem extends UIMenuItemBase {
    separator: true;
    space?: undefined;
    inline?: undefined;
    select?: undefined;
    title?: undefined;
    header?: undefined;
    widget?: undefined;
}

export interface UISpaceMenuItem extends UIMenuItemBase {
    separator?: undefined;
    space: number;
    inline?: undefined;
    select?: undefined;
    title?: undefined;
    header?: undefined;
    widget?: undefined;
}

export interface UIHeaderMenuItem extends UIMenuItemBase {
    separator?: undefined;
    space?: undefined;
    inline?: undefined;
    select?: undefined;
    title?: undefined;
    widget?: undefined;
    header: string;
}

export interface UIInlineMenuItem extends UIMenuItemBase {
    separator?: undefined;
    space?: undefined;
    inline: UIMenu;
    select?: undefined;
    title?: undefined;
    widget?: undefined;
    header?: undefined;
}

export interface UISelectMenuItem<Key> extends UIMenuItemBase {
    separator?: undefined;
    space?: undefined;
    inline?: undefined;
    select: UIValue<Key>[];
    title?: undefined;
    widget?: undefined;
    header?: undefined;

    value: Key;
    display?: (value: Key) => UIViewAddSpec | undefined;
    changed?: (value: Key) => void;
}

export interface UIButtonMenuItemBase extends UIMenuItemBase {
    separator?: undefined;
    space?: undefined;
    inline?: undefined;
    select?: undefined;
    title: string | (() => string);
    widget?: undefined;
    header?: undefined;

    icon?: UIIcon;
    highlight?: () => boolean;
}

export interface UIActionMenuItem extends UIButtonMenuItemBase {
    action: () => void;
    link?: undefined;
    popup?: undefined;
    submenu?: undefined;
}

export interface UIKeyMenuItem extends UIButtonMenuItemBase {
    key: string;
    action?: () => void;
    link?: undefined;
    popup?: undefined;
    submenu?: undefined;
}

export interface UIPopupMenuItem extends UIButtonMenuItemBase {
    action?: undefined;
    link?: undefined;
    popup: () => UIViewSpec | Promise<UIViewSpec>;
    submenu?: undefined;
}

export interface UISubmenuMenuItem extends UIButtonMenuItemBase {
    action?: undefined;
    link?: undefined;
    popup?: undefined;
    submenu: UIMenu | Promise<UIMenu> | (() => UIMenu | Promise<UIMenu>);
}

export interface UILinkMenuItem extends UIButtonMenuItemBase {
    action?: undefined;
    link: UILink;
    popup?: undefined;
    submenu?: undefined;
}

export interface UICheckMenuItem extends UIButtonMenuItemBase {
    action?: undefined;
    link?: undefined;
    popup?: undefined;
    submenu?: undefined;

    checked: boolean;
    changed?: (checked: boolean) => void;
}

export interface UIWidgetMenuItem extends UIMenuItemBase {
    separator?: undefined;
    space?: undefined;
    inline?: undefined;
    select?: undefined;
    title?: undefined;
    header?: undefined;
    widget: () => UIViewSpec;
}

export type UIMenuFlattenedItem = UISeparatorMenuItem | UISpaceMenuItem | UIHeaderMenuItem | UISelectMenuItem<any> | UIActionMenuItem | UIPopupMenuItem | UISubmenuMenuItem | UILinkMenuItem | UICheckMenuItem | UIKeyMenuItem | UIWidgetMenuItem;
export type UIMenuItem = UIMenuFlattenedItem | UIInlineMenuItem;

export type UIMenuListener = (i?: UIMenuItem) => void;

declare global {
    interface Element {
        ui_menu_listeners?: Record<number, UIMenuListener>;
    }
}

let next_menu_id = 1;

export class UIMenu {
    private _id: number;
    private _items: UIMenuItem[];
    private _hidden: boolean;
    on_option?: (i: UIMenuItem) => void;
    secondary?: () => UIMenu | undefined;
    first_path_func?: () => string[] | undefined;

    constructor(items?: UIMenuItem[], on_option?: (i: UIMenuItem) => void) {
        this._items = items || [];
        this._id = next_menu_id++;
        this._hidden = false;
        this.on_option = on_option;
    }

    addListener(where: UIViewSpec, listener: UIMenuListener) {
        const id = this._id;
        where = where.ui_elem;
        where.classList.add(`e-menu-{id}`);
        where.ui_menu_listeners ||= {};
        where.ui_menu_listeners[id] = listener;
    }

    update(item?: UIMenuItem, skip?: UIViewSpec) {
        const id = this._id;

        skip = skip?.ui_elem;

        for(const w of document.getElementsByClassName(`e-menu-{id}`)) {
            if(w != skip)
                w.ui_menu_listeners?.[id]?.(item);
        }
    }

    get items(): UIMenuItem[] {
        return this._items;
    }

    set items(items: UIMenuItem[]) {
        this._items = items;
        this.update();
    }

    get hidden(): boolean {
        return this._hidden;
    }

    set hidden(hidden: boolean) {
        this._hidden = hidden;
        this.update();
    }

    get flattened_items(): [UIMenu, UIMenuFlattenedItem][] {
        const items: [UIMenu, UIMenuFlattenedItem][] = [];

        if(!this._hidden) {
            for(const i of this.items) {
                if(yResolve(i.when) === false) continue;

                if(i.inline)
                    items.push(...i.inline.flattened_items);
                else
                    items.push([this, i]);
            }
        }

        return items;
    }

    get first_path(): string[] | undefined {
        return this.first_path_func?.();
    }
}

function makeMenuItem(item: UIMenuFlattenedItem, dropdown: UIDropdown, menu: UIMenu): UIDropdownItem | undefined {
    if(item.separator || item.space != undefined) {
        return { view: $el.DIV('ui-dropdown-separator'), disabled: true }
    } else if(item.header != undefined) {
        return { view: $el.DIV('ui-dropdown-header', item.header), disabled: true }
    } else if(item.widget) {
        return;
    } else if(item.select) {
        // const select = $ui.SELECT(item.select, item.value);
        // select.addEventListener('change', () => menu.update(item));
        // return {view: select};
        return;
    } else {
        const content: UIViewAddSpec = [
            $el.SPAN('r-title', yResolve(item.title)),
            item.icon && $el.ICON(item.icon, 'r-icon')
        ]

        const view = item.link ? $el.A(item.link, content, 'ui-dropdown-item') : $el.SPAN('ui-dropdown-item', content);
        if(item.icon)
            view.classList.add('m-icon');
        let on_choose: (() => void) | undefined;

        if(item.popup || item.submenu) {
            view.classList.add('m-sub');
            view.ui_add($el.ICON(UIIcon.ANGLE_RIGHT, 'r-sub'));
        }

        if(item.popup) {
            on_choose = () => {
                const popup = item.popup();
                if(popup instanceof Promise) {
                    popup.then(p => {
                        uiShowPopup(p, view, { position: UIRelPos.Right | UIRelPos.Start, capture_mouse: true, can_flip: true, parent: dropdown });
                    });
                } else {
                    uiShowPopup(popup, view, { position: UIRelPos.Right | UIRelPos.Start, capture_mouse: true, can_flip: true, parent: dropdown });
                }
                return false;
            }
        } else if(item.submenu) {
            on_choose = () => {
                const submenu = yResolve(item.submenu);
                if(submenu instanceof Promise) {
                    submenu.then(submenu => { uiShowMenu(submenu, view, UIRelPos.Right | UIRelPos.Start, dropdown) });
                } else {
                    uiShowMenu(submenu, view, UIRelPos.Right | UIRelPos.Start, dropdown);
                }
                return false;
            }
        } else if('checked' in item) {
            view.classList.add('m-check');
            view.classList.toggle('f-checked', item.checked);

            on_choose = () => {
                item.checked = !item.checked;
                const new_value = item.checked;
                view.classList.toggle('f-checked', new_value);
                item.changed?.(new_value);
                menu.update(item);
            }
        } else {
            on_choose = () => {
                item.action?.();
                menu.on_option?.(item);
            }
        }

        // const item = i.link ? $el.A(i.link, i.title, 'ui-dropdown-item') : $el.SPAN('ui-dropdown-item', i.title);
        // if(i.submenu || i.popup) {
        // }

        return { view, on_choose }
    }
}

export function uiShowMenu(menu: UIMenu, ref: UIViewSpec, position: UIRelPos = UIRelPos.Below | UIRelPos.End, parent?: UIViewSpec, offset?: number) {
    const dropdown = new UIDropdown(menu.flattened_items, ([m, i], dropdown) => makeMenuItem(i, dropdown, m));
    dropdown.open(ref, { position: position, adjust_size: true, match_size: true, capture_mouse: true, can_flip: true, parent: parent, offset: offset });

    return dropdown;
}
